import { __assign } from "tslib";
import { OfficeActionType, } from '@office-start/action-types';
import { ShareControlMode } from '@office-start/share-types';
import { sanitizeUrlParameter } from '@office-start/url-parser-utility';
var defaultLinkTargetNewTab = '_blank';
/**
 * @description Create an office action with payload
 *
 * @todo - evlave - Allow for payload type to be: OfficeActionPayloadType | (...args[]: unknown) => OfficeActionPayloadType
 * or just use redux toolkit to do the same thing.
 *
 * @param type OfficeActionType of this action
 * @param payload OfficeActionPayloadType this action represents
 * @returns A new IOfficeActionWithPayload
 */
var createAction = function (type, payload, actionCreatorOptions) {
    return {
        type: type,
        payload: __assign(__assign({}, payload), actionCreatorOptions),
    };
};
var createLaunchUrlAction = function (url, target, actionCreatorOptions) {
    if (target === void 0) { target = defaultLinkTargetNewTab; }
    return createAction(OfficeActionType.externalUrl, {
        url: sanitizeUrlParameter(url),
        launchAction: 'OpenInBrowser',
        target: target,
    }, actionCreatorOptions);
};
var createLaunchExternalUrlInContextAction = function (props, options) {
    return createAction(OfficeActionType.externalUrlInContext, {
        props: props,
        nextAction: options === null || options === void 0 ? void 0 : options.nextAction,
    }, options);
};
var createLaunchAppInContextAction = function (appId, entityId, subEntityId, options, queryParams) {
    return createAction(OfficeActionType.contentAppInContext, {
        appId: appId,
        entityId: entityId,
        subEntityId: subEntityId,
        target: '_self',
        queryParams: queryParams,
    }, options);
};
var createContentUrlAction = function (url, isEmailAttachment, target, options) {
    if (target === void 0) { target = defaultLinkTargetNewTab; }
    return createAction(OfficeActionType.contentUrl, {
        url: sanitizeUrlParameter(url),
        launchAction: isEmailAttachment ? 'OpenAttachment' : 'OpenFile',
        target: target,
    }, options);
};
var createContentDownloadAction = function (url, driveId, itemId, sharePointIds, target, tooltipString, options) {
    if (target === void 0) { target = defaultLinkTargetNewTab; }
    return createAction(OfficeActionType.contentDownload, {
        url: sanitizeUrlParameter(url),
        launchAction: 'Download',
        target: target,
        driveId: driveId,
        itemId: itemId,
        sharePointIds: sharePointIds,
        tooltipString: tooltipString,
    }, options);
};
var createContentNativeDownloadAction = function (url, target, options) {
    if (target === void 0) { target = defaultLinkTargetNewTab; }
    return createAction(OfficeActionType.contentNative, {
        url: sanitizeUrlParameter(url),
        launchAction: 'OpenInApp',
        target: target,
    }, options);
};
var createContentLocationAction = function (url, target, tooltipString, options) {
    if (target === void 0) { target = defaultLinkTargetNewTab; }
    return createAction(OfficeActionType.contentLocation, {
        url: sanitizeUrlParameter(url),
        launchAction: 'OpenFileLocation',
        target: target,
        tooltipString: tooltipString,
    }, options);
};
var createContentLocationLocalAction = function (url, options) {
    return createAction(OfficeActionType.contentLocationLocal, { url: sanitizeUrlParameter(url) }, options);
};
var createContentOpenAction = function (content, options) {
    return createAction(OfficeActionType.contentOpen, { content: content }, options);
};
var createCopyToClipboardAction = function (data, options) { return createAction(OfficeActionType.copyToClipboard, { data: data }, options); };
var createDeleteAction = function (content, driveId, itemId, sharePointIds, tooltipString, options) {
    return createAction(OfficeActionType.delete, {
        content: content,
        driveId: driveId,
        itemId: itemId,
        sharePointIds: sharePointIds,
        tooltipString: tooltipString,
    }, options);
};
var createDeleteRestoreAction = function (_a) {
    var content = _a.content, options = _a.options;
    return createAction(OfficeActionType.deleteRestore, {
        content: content,
    }, options);
};
var createLinkContentOpenAction = function (content, options) { return createAction(OfficeActionType.linkContentOpen, { content: content }, options); };
var createNavigateToApp = function (appId, entityId, subEntityId, options, queryParams) {
    return createAction(OfficeActionType.deepLinkToApp, {
        appId: appId,
        entityId: entityId,
        subEntityId: subEntityId,
        target: '_self',
        queryParams: queryParams,
    }, options);
};
var createNavigateToAppExternal = function (appId, entityId, target, subEntityId, options, queryParams) {
    if (target === void 0) { target = '_blank'; }
    return createAction(OfficeActionType.deepLinkToAppExternal, {
        appId: appId,
        entityId: entityId,
        subEntityId: subEntityId,
        target: target,
        queryParams: queryParams,
    }, options);
};
var createShareOrCopyAction = function (companyDisplayName, fileName, odspShareInfo, mode, elementId, containerStyles, fileType, tooltipString, options, shareUIPositionOverride) {
    if (mode === void 0) { mode = ShareControlMode.Share; }
    return createAction(OfficeActionType.contentShareInContext, {
        companyDisplayName: companyDisplayName,
        elementId: elementId,
        containerStyles: containerStyles,
        name: fileName,
        fileType: fileType,
        fileName: fileName,
        mode: mode,
        odspShareInfo: odspShareInfo,
        tooltipString: tooltipString,
        shareUIPositionOverride: shareUIPositionOverride,
    }, options);
};
var createConvertDriveItemAction = function (driveId, itemId, originalFileNameWithoutExtension, originalFileExtension, format, sharePointIds, isRedeemed, url, options) {
    return createAction(OfficeActionType.convertDriveItem, {
        driveId: driveId,
        format: format,
        isRedeemed: isRedeemed,
        itemId: itemId,
        originalFileExtension: originalFileExtension,
        originalFileNameWithoutExtension: originalFileNameWithoutExtension,
        sharePointIds: sharePointIds,
        url: url,
    }, options);
};
var createContentShareToTeamsAction = function (url, title, dialogStrings, shouldOpenExternal, createSharingLinkApiEnabled, companyDisplayName, driveId, itemId, siteId, isRedeemed, options) {
    return createAction(OfficeActionType.openShareToTeams, {
        createSharingLinkApiEnabled: createSharingLinkApiEnabled,
        driveId: driveId,
        shouldOpenExternal: shouldOpenExternal,
        itemId: itemId,
        siteId: siteId,
        title: title,
        url: url,
        dialogStrings: dialogStrings,
        companyDisplayName: companyDisplayName,
        isRedeemed: isRedeemed,
    }, options);
};
var createTodoAction = function (title, url, listId, applicationName, options) {
    return createAction(OfficeActionType.createTodo, {
        applicationName: applicationName,
        listId: listId,
        title: title,
        url: sanitizeUrlParameter(url),
    }, options);
};
var createScheduleAction = function (title, url, isRedeemed, options) {
    return createAction(OfficeActionType.schedule, {
        title: title,
        url: url,
        isRedeemed: isRedeemed,
    }, options);
};
var createOpenTaskModuleAction = function (appId, url, height, width, additionalParams, options) {
    return createAction(OfficeActionType.openTaskModule, {
        appId: appId,
        url: url,
        height: height,
        width: width,
        additionalParams: additionalParams,
    }, options);
};
export var OfficeActionCreators = {
    createContentUrlAction: createContentUrlAction,
    createContentDownloadAction: createContentDownloadAction,
    createContentNativeDownloadAction: createContentNativeDownloadAction,
    createContentLocationAction: createContentLocationAction,
    createContentLocationLocalAction: createContentLocationLocalAction,
    createContentOpenAction: createContentOpenAction,
    createConvertDriveItemAction: createConvertDriveItemAction,
    createCopyToClipboardAction: createCopyToClipboardAction,
    createDeleteAction: createDeleteAction,
    createDeleteRestoreAction: createDeleteRestoreAction,
    createLaunchAppInContextAction: createLaunchAppInContextAction,
    createLaunchUrlAction: createLaunchUrlAction,
    createLaunchExternalUrlInContextAction: createLaunchExternalUrlInContextAction,
    createLinkContentOpenAction: createLinkContentOpenAction,
    createNavigateToApp: createNavigateToApp,
    createNavigateToAppExternal: createNavigateToAppExternal,
    createOpenTaskModuleAction: createOpenTaskModuleAction,
    createShareOrCopyAction: createShareOrCopyAction,
    createContentShareToTeamsAction: createContentShareToTeamsAction,
    createTodoAction: createTodoAction,
    createScheduleAction: createScheduleAction,
};
//# sourceMappingURL=officeActionCreators.js.map